import type { ReactElement } from 'react';
import { Terminal } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { AccentCard } from './AccentCard';
import { AlertEvidenceExcerpt } from './AlertEvidenceExcerpt';
import { TOOL_CALL_LABELS } from './tool-call-copy';

interface ToolCallCardProps {
  /** The tool that was called, e.g. `Bash`. */
  toolName?: string;
  /** Derived verdict, or null when the alert carries no score/threshold. */
  verdict: 'flagged' | 'allowed' | null;
  /** PII-screened excerpt of the tool input — the command, for a Bash call. */
  excerpt?: string;
}

/**
 * KIR-4836 — the first card of the tool-call panel: what was called, how it was
 * ruled on, and the command itself.
 *
 * The ticket's guiding note is "lead with the command and the reason", so this
 * sits directly under "What this means", ahead of any scores. It replaces the
 * old presentation, where the same command was a labelled sub-block buried at
 * the bottom of the generic "Detected on client" card.
 */
export function ToolCallCard({
  toolName,
  verdict,
  excerpt,
}: ToolCallCardProps): ReactElement {
  return (
    <AccentCard category="tool-call" title={TOOL_CALL_LABELS.toolCallCard}>
      {(toolName !== undefined || verdict !== null) && (
        <div className="flex flex-wrap items-center gap-2">
          {toolName !== undefined && (
            <Badge
              variant="outline"
              className="gap-1.5 font-mono"
              data-testid="tool-call-tool-chip"
            >
              <Terminal className="h-3.5 w-3.5" aria-hidden />
              {toolName}
            </Badge>
          )}
          {verdict !== null && (
            <Badge
              variant={verdict === 'flagged' ? 'destructive' : 'secondary'}
              className="uppercase tracking-wide"
              data-testid="tool-call-verdict-chip"
            >
              {verdict === 'flagged'
                ? TOOL_CALL_LABELS.flagged
                : TOOL_CALL_LABELS.allowed}
            </Badge>
          )}
        </div>
      )}
      {excerpt !== undefined && (
        /* No label: the card title already says "Tool call". The accent border
           ties the command back to that title, as in the mockup. */
        <AlertEvidenceExcerpt
          content={excerpt}
          className="max-w-full border-l-2 border-l-rose-400 dark:border-l-rose-500"
        />
      )}
    </AccentCard>
  );
}

ToolCallCard.displayName = 'ToolCallCard';
